import { Helmet, HelmetData } from "react-helmet-async";
import { AI_BG_NEW } from "../../../assets";
const helmetData = new HelmetData({});

export default function InfoSecurityPage() {
  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>ISMS &amp; BCMS | Analytics Intelligence Ltd — AI Compliance &amp; Information Security</title>
        <link rel="canonical" href="https://analyticsintelligence.com/isms-bcms" />
        <meta
          name="description"
          content="Analytics Intelligence Ltd ensures enterprise trust through ISO/IEC 27001 ISMS and ISO 22301 BCMS frameworks — AI compliance solutions, data protection, and business continuity for banking, fintech, and financial institutions."
        />
        <meta
          name="keywords"
          content="Analytics Intelligence, Analytics Intelligence Ltd, ISMS, BCMS, AI compliance solutions, AI compliance for financial institutions, information security management, business continuity management, ISO 27001, ISO 22301, cybersecurity, data protection, enterprise resilience, data governance solutions for banks, regulatory reporting automation with AI, AI company in Mauritius, fintech AI solutions Mauritius"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Analytics Intelligence Ltd" />
        <meta property="og:title" content="ISMS & BCMS | Analytics Intelligence Ltd — AI Compliance & Information Security" />
        <meta property="og:description" content="ISO 27001 ISMS and ISO 22301 BCMS compliance by Analytics Intelligence Ltd — AI compliance solutions for banking, fintech, and enterprise organisations." />
        <meta property="og:url" content="https://analyticsintelligence.com/isms-bcms" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://analyticsintelligence.com/favicon.png" />
        <meta property="og:site_name" content="Analytics Intelligence" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@_AnalyticsIntel" />
        <meta name="twitter:title" content="ISMS & BCMS | Analytics Intelligence Ltd — AI Compliance & Information Security" />
        <meta name="twitter:description" content="ISO 27001 ISMS and ISO 22301 BCMS compliance by Analytics Intelligence Ltd — AI compliance solutions for banking, fintech, and enterprise organisations." />
        <meta name="twitter:image" content="https://analyticsintelligence.com/favicon.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "ISMS & BCMS | Analytics Intelligence Ltd",
            "description": "Analytics Intelligence Ltd's Information Security Management System (ISMS) and Business Continuity Management System (BCMS) — ISO/IEC 27001 and ISO 22301 compliant AI for banking, fintech, and enterprise.",
            "url": "https://analyticsintelligence.com/isms-bcms",
            "publisher": {
              "@type": "Organization",
              "name": "Analytics Intelligence Ltd",
              "url": "https://analyticsintelligence.com"
            }
          }
        `}</script>
      </Helmet>

      <main>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
          className="min-h-[120vh] flex items-center"
        >
          <div className="container w-full h-full flex">
            {/* Sidebar */}
            <aside className="hidden md:block w-1/4 p-4 pt-0 sticky top-52 h-screen text-white">
              <ul className="space-y-2">
                <li>
                  <a href="#security" className="hover:underline">
                    Information Security & Business Continuity
                  </a>
                </li>
                <li>
                  <a href="#isms" className="hover:underline">
                    Information Security Management (ISMS)
                  </a>
                </li>
                <li>
                  <a href="#bcms" className="hover:underline">
                    Business Continuity Management (BCMS)
                  </a>
                </li>
                <li>
                  <a href="#stakeholders" className="hover:underline">
                    Our Commitment to Stakeholders
                  </a>
                </li>
              </ul>
            </aside>

            {/* Content */}
            <main className="w-full md:w-3/4 px-0 md:px-6 space-y-16 scroll-smooth pt-52 pb-[96px]">
              <section
                id="security"
                className=" flex flex-col gap-2 scroll-mt-28"
              >
                <h2 className="text-2xl font-extrabold text-white">
                  Information Security & Business Continuity
                </h2>
                <p className="text-base font-medium text-white">
                  At [AI] Analytics Intelligence, protecting information and
                  ensuring service resilience are at the core of how we operate.
                  We have established an Information Security Management System
                  (ISMS) and a Business Continuity Management System (BCMS),
                  both aligned with international standards such as ISO/IEC
                  27001 and ISO 22301. These frameworks provide our stakeholders
                  with confidence that security and continuity are not just
                  priorities, but integral parts of our culture and operations. 
                </p>
              </section>

              <section id="isms" className=" flex flex-col gap-2 scroll-mt-28">
                <h2 className="text-2xl font-extrabold text-white">
                  Information Security Management (ISMS)
                </h2>
                <div className="space-y-4">
                  <p className="text-base font-medium text-white">
                    Our ISMS ensures that information security risks are
                    systematically identified, assessed, and managed. We apply
                    security controls across people, processes, and technology
                    to protect the confidentiality, integrity, and availability
                    of information. 
                  </p>

                  <ul>
                    <p className="text-base font-medium text-white">
                      Key areas of focus include:
                    </p>
                    <li className="text-base font-medium text-white">
                      • Data Protection – safeguarding personal and sensitive
                      data.
                    </li>
                    <li className="text-base font-medium text-white">
                      • Access Control – ensuring only authorized individuals
                      have access to systems and information.
                    </li>
                    <li className="text-base font-medium text-white">
                      • Cybersecurity Measures – proactive monitoring,
                      vulnerability management, and secure development
                      practices.
                    </li>
                    <li className="text-base font-medium text-white">
                      • Legal Compliance – alignment with international
                      standards and legal/regulatory requirements and practices.
                    </li>
                  </ul>

                  <p className="text-base text-[#E3E3E3]">
                    Analytics Intelligence ensures all employees are
                    well-informed and trained on our security practices, and
                    policies are made accessible to partners and stakeholders as
                    needed. We review these policies regularly to stay
                    responsive to changes in regulations, business needs, and
                    other relevant factors.
                  </p>
                </div>
              </section>

              <section id="bcms" className=" flex flex-col gap-2 scroll-mt-28">
                <h2 className="text-2xl font-extrabold text-white">
                  Business Continuity Management (BCMS)
                </h2>
                <div className="space-y-4">
                  <p className="text-base font-medium text-white">
                    Our BCMS ensures that critical business functions remain
                    operational in the face of disruptions, whether from cyber
                    incidents, natural disasters, or unexpected events. By
                    maintaining resilience and recovery plans, we safeguard not
                    only our operations but also the interests of our
                    stakeholders.
                  </p>

                  <ul>
                    <p className="text-base font-medium text-white">
                      Key areas of focus include:
                    </p>
                    <li className="text-base font-medium text-white">
                      • Risk Assessment & Preparedness – identifying threats to
                      business operations and preparing mitigation measures.
                    </li>
                    <li className="text-base font-medium text-white">
                      • Disaster Recovery & Incident Response – tested plans to
                      restore services quickly and effectively.
                    </li>
                    <li className="text-base font-medium text-white">
                      • Redundancy & Backup – ensuring essential data and
                      systems remain available.
                    </li>
                    <li className="text-base font-medium text-white">
                      • Stakeholder Communication – keeping clients, partners,
                      and regulators informed during critical events.
                    </li>
                  </ul>

                  <p className="text-base text-[#E3E3E3]">
                    Analytics Intelligence ensures all employees are
                    well-informed and trained on our security practices, and
                    policies are made accessible to partners and stakeholders as
                    needed. We review these policies regularly to stay
                    responsive to changes in regulations, business needs, and
                    other relevant factors.
                  </p>
                </div>
              </section>

              <section
                id="stakeholders"
                className=" flex flex-col gap-2 scroll-mt-28"
              >
                <h2 className="text-2xl font-extrabold text-white">
                  Our Commitment to Stakeholders
                </h2>
                <div className="space-y-4">
                  <ul>
                    <p className="text-base font-medium text-white">
                      By integrating ISMS and BCMS, we provide a comprehensive
                      assurance framework that demonstrates:
                    </p>
                    <li className="text-base font-medium text-white">
                      • We protect information assets with recognized best
                      practices.
                    </li>
                    <li className="text-base font-medium text-white">
                      • We are prepared to continue operations and recover
                      quickly in case of disruptions.
                    </li>
                    <li className="text-base font-medium text-white">
                      • We regularly test, review, and improve our controls and
                      continuity measures.
                    </li>
                  </ul>

                  <p className="text-base text-[#E3E3E3]">
                    Our commitment is to provide trust, transparency, and
                    resilience to all who rely on our services. For more
                    information, or to request copies of our ISMS/BCMS policies,
                    please contact: info@analyticsintelligence.com
                  </p>
                </div>
              </section>
            </main>
          </div>
        </section>
      </main>
    </>
  );
}
